// src/features/tuition/components/online/MicroActivityList.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Tooltip
} from '@mui/material';
import {
  QuestionAnswer as QuizIcon,
  Comment as ReflectionIcon,
  CheckCircle as CheckpointIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  SportsScore as PointsIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';

const MicroActivityList = ({ activities = [], onEditActivity, onDeleteActivity, disabled = false }) => {
  if (activities.length === 0) {
    return (
      <Box 
        sx={{ 
          py: 6, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.02)',
          borderRadius: 2,
          border: '1px dashed #ccc'
        }}
      >
        <Typography variant="body1" color="text.secondary" gutterBottom>
          No activities added yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add activities to engage students and track their progress
        </Typography>
      </Box>
    );
  }
  
  const getActivityIcon = (type) => {
    switch (type) {
      case 'quiz':
        return <QuizIcon sx={{ color: '#2196F3' }} />;
      case 'reflection':
        return <ReflectionIcon sx={{ color: '#FF9800' }} />;
      case 'checkpoint':
        return <CheckpointIcon sx={{ color: '#4CAF50' }} />;
      default:
        return <QuizIcon sx={{ color: '#2196F3' }} />;
    }
  };
  
  const getActivityTypeLabel = (type) => {
    switch (type) {
      case 'quiz':
        return 'Multiple Choice Quiz';
      case 'reflection':
        return 'Reflection Question';
      case 'checkpoint':
        return 'Checkpoint';
      default:
        return 'Activity';
    }
  };
  
  return (
    <List sx={{ width: '100%', bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 2 }}>
      {activities.map((activity, index) => (
        <React.Fragment key={activity.id || index}>
          <ListItem
            alignItems="flex-start"
            sx={{
              px: 2,
              py: 2
            }}
          >
            <ListItemIcon>
              {getActivityIcon(activity.type)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="medium">
                    {activity.title}
                  </Typography>
                  <Chip 
                    label={getActivityTypeLabel(activity.type)} 
                    size="small" 
                    sx={{ 
                      ml: 2,
                      backgroundColor: 
                        activity.type === 'quiz' ? 'rgba(33, 150, 243, 0.08)' : 
                        activity.type === 'reflection' ? 'rgba(255, 152, 0, 0.08)' : 
                        'rgba(76, 175, 80, 0.08)',
                      color: 
                        activity.type === 'quiz' ? '#2196F3' : 
                        activity.type === 'reflection' ? '#FF9800' : 
                        '#4CAF50',
                    }}
                  />
                  {activity.isRequired && (
                    <Chip 
                      label="Required" 
                      size="small" 
                      sx={{ ml: 1, bgcolor: 'rgba(244, 67, 54, 0.08)', color: '#F44336' }}
                    />
                  )}
                </Box>
              }
              secondary={
                <>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.primary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {activity.description}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Tooltip title="Time to complete">
                      <Box display="flex" alignItems="center" mr={2}>
                        <TimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {activity.timeToComplete} mins
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip title="Points">
                      <Box display="flex" alignItems="center">
                        <PointsIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {activity.points} pts
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                  {activity.type === 'quiz' && activity.options && activity.options.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Options:
                      </Typography>
                      <Box ml={2}>
                        {activity.options.map((option, optIndex) => (
                          <Typography 
                            key={optIndex} 
                            variant="body2" 
                            color={option === activity.correctAnswer ? 'success.main' : 'text.secondary'}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              mb: 0.5
                            }}
                          >
                            • {option} {option === activity.correctAnswer && ' (Correct)'}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              }
            />
            <ListItemSecondaryAction>
              <Box>
                <IconButton 
                  edge="end" 
                  aria-label="edit"
                  onClick={() => onEditActivity(activity)}
                  disabled={disabled}
                  sx={{ color: THEME_COLORS.primary }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  edge="end" 
                  aria-label="delete"
                  onClick={() => onDeleteActivity(index)}
                  disabled={disabled}
                  sx={{ color: '#F44336' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
          {index < activities.length - 1 && <Divider component="li" />}
        </React.Fragment>
      ))}
    </List>
  );
};

export default MicroActivityList;